import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(() => localStorage.getItem("auth_token") || null);

  const refresh = useCallback(async () => {
    if (!token) { setUser(false); return null; }
    try {
      const { data } = await api.get("/auth/me", { headers: { Authorization: `Bearer ${token}` } });
      setUser(data);
      return data;
    } catch {
      setUser(false);
      return null;
    }
  }, [token]);

  useEffect(() => { refresh(); }, [refresh]);

  const login = async (email, password, practice_code = null) => {
    const body = { email, password };
    if (practice_code) body.practice_code = practice_code;
    const { data } = await api.post("/auth/login", body);
    if (data.mode === "kiosk") {
      // Kiosk: kein Admin-Token speichern, nur kiosk_token Cookie ist gesetzt.
      localStorage.removeItem("auth_token");
      setToken(null);
      setUser(false);
      return data;
    }
    localStorage.setItem("auth_token", data.token);
    setToken(data.token);
    setUser(data);
    return data;
  };

  const completeRegistration = async (regToken, practice_name, password) => {
    const { data } = await api.post("/trial/complete", { token: regToken, practice_name, password });
    localStorage.setItem("auth_token", data.token);
    setToken(data.token);
    try {
      const me = await api.get("/auth/me", { headers: { Authorization: `Bearer ${data.token}` } });
      setUser(me.data);
    } catch { /* noop */ }
    return data;
  };

  const adoptToken = useCallback(async (newToken) => {
    localStorage.setItem("auth_token", newToken);
    setToken(newToken);
    try {
      const me = await api.get("/auth/me", { headers: { Authorization: `Bearer ${newToken}` } });
      setUser(me.data);
      return me.data;
    } catch { return null; }
  }, []);

  const logout = async () => {
    try { await api.post("/auth/logout", {}, { headers: token ? { Authorization: `Bearer ${token}` } : {} }); } catch { /* noop */ }
    localStorage.removeItem("auth_token");
    setToken(null);
    setUser(false);
  };

  const authHeaders = token ? { Authorization: `Bearer ${token}` } : {};

  return (
    <AuthContext.Provider value={{ user, token, login, completeRegistration, logout, authHeaders, refresh, adoptToken }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
